import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase } from '../../utils/apiBase';
import { setSelfPersonOnServer, dismissSelfPrompt } from '../../utils/selfPersonLink';

const API = getApiBase();

/**
 * Gentle onboarding prompt: "Would you like to add yourself to this family archive?"
 *
 * Props:
 *  trees      — array of tree objects the user owns (to pick which tree to add to)
 *  onLinked   — called with (personId, treeId) when self-link is established
 *  onDismiss  — called when user picks "Building for someone else" or "Skip"
 */
export function SelfPersonPrompt({ trees = [], onLinked, onDismiss }) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState('prompt'); // 'prompt' | 'add-form'
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedTreeId, setSelectedTreeId] = useState('');
  const [busy, setBusy] = useState(false);

  // Pre-fill name from account
  useEffect(() => {
    if (!user?.name) return;
    const parts = user.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      setFirstName(parts.slice(0, -1).join(' '));
      setLastName(parts[parts.length - 1]);
    } else {
      setFirstName(parts[0] || '');
      setLastName('');
    }
  }, [user?.name]);

  // Default to first tree
  useEffect(() => {
    if (trees.length > 0 && !selectedTreeId) {
      setSelectedTreeId(trees[0].id);
    }
  }, [trees, selectedTreeId]);

  const handleAddMyself = async () => {
    if (!selectedTreeId) {
      toast.error('Please select a family tree to add yourself to.');
      return;
    }
    const givenNames = firstName.trim();
    const surname = lastName.trim();
    if (!givenNames) {
      toast.error('Please enter at least a first name.');
      return;
    }
    setBusy(true);
    try {
      const resp = await axios.post(`${API}/individuals`, {
        tree_id: selectedTreeId,
        given_names: givenNames,
        surname: surname || '',
        profile_type: 'person',
        identity_status: 'claimed',
      });
      const personId = resp.data?.id;
      if (!personId) throw new Error('No person ID returned');

      await setSelfPersonOnServer(personId);
      await refreshUser();

      toast.success(`${givenNames}${surname ? ' ' + surname : ''} added to your archive.`, {
        description: 'You can now add your stories, photos, and details.',
        duration: 5000,
      });
      onLinked?.(personId, selectedTreeId);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not add your profile. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleOther = () => {
    dismissSelfPrompt('other');
    onDismiss?.('other');
  };

  const handleSkip = () => {
    dismissSelfPrompt('skip');
    onDismiss?.('skip');
  };

  if (step === 'add-form') {
    return (
      <div
        className="rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-[hsl(42_28%_99%)] px-5 py-5 shadow-sm sm:px-6"
        data-testid="self-person-add-form"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-serif text-[15px] font-medium text-[#1c1917]">Add yourself to this archive</p>
            <p className="mt-0.5 text-[12px] text-[#6b5f52]">
              We&apos;ve pre-filled your name from your account — adjust if needed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setStep('prompt')}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
            aria-label="Back"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              First name(s)
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30"
              placeholder="First name"
              disabled={busy}
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Last name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30"
              placeholder="Last name (optional)"
              disabled={busy}
            />
          </div>
        </div>

        {trees.length > 1 && (
          <div className="mt-3">
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Add to archive
            </label>
            <select
              value={selectedTreeId}
              onChange={(e) => setSelectedTreeId(e.target.value)}
              className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-foreground focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30"
              disabled={busy}
            >
              {trees.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="rounded-full"
            onClick={handleAddMyself}
            disabled={busy || !firstName.trim()}
          >
            {busy ? 'Adding…' : 'Add me to the archive'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full text-muted-foreground"
            onClick={() => setStep('prompt')}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Initial prompt
  return (
    <div
      className="rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-gradient-to-br from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] px-5 py-5 shadow-sm sm:px-6"
      data-testid="self-person-prompt"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <User className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="font-serif text-[15px] font-medium text-[#1c1917]">
              Would you like to add yourself to this family archive?
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-[#6b5f52]">
              Your MyLegacyBridge account and your person profile are separate. You can add yourself
              as a person — or simply build this archive for your family.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSkip}
          className="mt-0.5 rounded-full p-1 text-muted-foreground/60 hover:bg-muted/50 hover:text-muted-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          onClick={() => setStep('add-form')}
          data-testid="self-prompt-add-btn"
        >
          Add myself as a person
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full border-[hsl(38_14%_82%)] text-muted-foreground hover:text-foreground"
          onClick={handleOther}
          data-testid="self-prompt-other-btn"
        >
          I&apos;m building this for someone else
        </Button>
        <button
          type="button"
          className="text-[12px] text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
          onClick={handleSkip}
          data-testid="self-prompt-skip-btn"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

export default SelfPersonPrompt;
